import { useCallback, useEffect, useReducer } from 'react';

import { cfetch } from '@confluence/network';
import { fg } from '@confluence/feature-gating';

export type AvailableManagedLabel = {
	id: string;
	name: string;
	kind: string;
	// Optional: the org external-labels endpoint may omit status. When absent the
	// label is treated as usable (see the filter in managedLabelsPickerOptions).
	status?: string;
};

// The org external-labels endpoint returns a connection object
// ({ labels: [...], totalCount }), mirroring the applied-labels GraphQL shape.
type OrgExternalLabelsResponse = {
	labels?: AvailableManagedLabel[] | null;
	totalCount?: number | null;
};

type UseAvailableManagedLabelsResult = {
	availableLabels: AvailableManagedLabel[];
	loading: boolean;
	error: Error | null;
	refetch: () => void;
};

type State = {
	availableLabels: AvailableManagedLabel[];
	loading: boolean;
	error: Error | null;
};

type Action =
	| { type: 'FETCH_START' }
	| { type: 'FETCH_SUCCESS'; payload: AvailableManagedLabel[] }
	| { type: 'FETCH_ERROR'; payload: Error };

const initialState: State = { availableLabels: [], loading: false, error: null };

function reducer(state: State, action: Action): State {
	switch (action.type) {
		case 'FETCH_START':
			return { ...state, loading: true, error: null };
		case 'FETCH_SUCCESS':
			return { availableLabels: action.payload, loading: false, error: null };
		case 'FETCH_ERROR':
			return { availableLabels: [], loading: false, error: action.payload };
		default:
			return state;
	}
}

const ORG_EXTERNAL_LABELS_URL = '/wiki/rest/internal/latest/org/external-labels';

/**
 * Fetches all available managed (external) labels for the org.
 *
 * Uses the Confluence internal REST endpoint
 * `GET /wiki/rest/internal/latest/org/external-labels`, which returns a
 * connection object (`{ labels: [...], totalCount }`) of the admin-created
 * labels that can be applied to content. A bare array response is also
 * tolerated for backwards compatibility.
 *
 * Gated behind the `confluence_managed_external_labels_endpoint` feature gate.
 */
export const useAvailableManagedLabels = (): UseAvailableManagedLabelsResult => {
	const [state, dispatch] = useReducer(reducer, initialState);

	const fetchAvailableLabels = useCallback(async () => {
		if (!fg('confluence_managed_external_labels_endpoint')) {
			return;
		}

		dispatch({ type: 'FETCH_START' });

		try {
			const response = await cfetch(ORG_EXTERNAL_LABELS_URL);

			if (!response.ok) {
				throw new Error(`Failed to fetch available managed labels: ${response.status}`);
			}

			const data: OrgExternalLabelsResponse | AvailableManagedLabel[] = await response.json();
			// Prefer the connection shape ({ labels }); fall back to a bare array.
			const labels = Array.isArray(data) ? data : (data?.labels ?? []);
			dispatch({ type: 'FETCH_SUCCESS', payload: Array.isArray(labels) ? labels : [] });
		} catch (err) {
			dispatch({
				type: 'FETCH_ERROR',
				payload: err instanceof Error ? err : new Error(String(err)),
			});
		}
	}, []);

	useEffect(() => {
		void fetchAvailableLabels();
	}, [fetchAvailableLabels]);

	return {
		availableLabels: state.availableLabels,
		loading: state.loading,
		error: state.error,
		refetch: fetchAvailableLabels,
	};
};
